import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import cn from 'classnames'
import {
   FlowIcon,
   TFlowIcon,
} from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import { useFlowBase } from '@forwardflow/features/campaign-flow/hooks/useFlowBase'
import { TRailKey } from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import {
   ASKED_ROWS,
   IRailRow,
   READY_ROWS,
} from '@forwardflow/features/campaign-flow/utils/rail'

/* ---------- Top bar: back, breadcrumb, status ---------- */

export const TopBar = ({
   name,
   right,
}: {
   name: string
   right?: ReactNode
}) => {
   const { preview, campaigns } = useFlowBase()
   return (
      <div
         className="bg-white/[0.05] h-[60px] rounded-xl flex items-center px-3 md:px-4 gap-2 shrink-0"
         data-testid="cf-topbar"
      >
         <Link
            to={campaigns}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 text-white"
            aria-label="Back to campaigns"
         >
            <FlowIcon name="back" size={18} />
         </Link>
         <nav className="flex items-center gap-2 text-sm ml-1 md:ml-2 min-w-0">
            <Link
               to={preview ? campaigns : '/dashboard'}
               className="text-[#9ca3af] hover:text-white hidden sm:inline"
            >
               Home
            </Link>
            <span className="text-white/25 hidden sm:inline">/</span>
            <Link to={campaigns} className="text-[#9ca3af] hover:text-white">
               Campaigns
            </Link>
            <span className="text-white/25">/</span>
            <b
               className="font-semibold text-white truncate"
               data-testid="cf-name"
            >
               {name}
            </b>
         </nav>
         <div className="ml-auto flex items-center gap-2">{right}</div>
      </div>
   )
}

/* ---------- Rail ---------- */

const RailRow = ({
   row,
   state,
   value,
   onClick,
   n,
}: {
   row: IRailRow
   state: 'cur' | 'done' | 'todo' | 'ready'
   value?: string
   onClick?: () => void
   n?: number
}) => (
   <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={`cf-rail-${row.key}`}
      data-state={state}
      className={cn(
         'w-full flex items-center gap-3 px-3 h-[60px] rounded-xl text-left min-w-[190px] lg:min-w-0 transition-colors',
         state === 'cur'
            ? 'bg-[#9061DA]/[0.18]'
            : onClick
              ? 'hover:bg-white/[0.04]'
              : 'cursor-default'
      )}
   >
      <span
         className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-[13px] font-semibold',
            state === 'cur' && 'bg-[#9061DA] text-white',
            (state === 'done' || state === 'ready') &&
               'bg-[#00d857]/[0.14] text-[#00d857]',
            state === 'todo' && 'bg-white/[0.06] text-white/40'
         )}
      >
         {state === 'todo' && n ? n : <FlowIcon name={row.icon} size={14} />}
      </span>
      <span className="flex-1 min-w-0">
         <span
            className={cn(
               'block text-sm font-semibold leading-[18px] truncate',
               state === 'todo' && 'text-white/40'
            )}
         >
            {row.label}
         </span>
         {value && (
            <span
               className={cn(
                  'block text-xs leading-4 mt-0.5 truncate',
                  state === 'cur' ? 'text-[#DAC2FF]' : 'text-[#9ca3af]'
               )}
            >
               {value}
            </span>
         )}
      </span>
      {onClick && state !== 'cur' && (
         <span className="text-xs font-semibold text-[#DAC2FF]">
            {state === 'ready' ? 'Change' : 'Edit'}
         </span>
      )}
   </button>
)

export const Rail = ({
   current,
   done,
   values,
   onGo,
   locked,
}: {
   current: TRailKey
   done: TRailKey[]
   values: Partial<Record<TRailKey, string>>
   onGo: (key: TRailKey) => void
   /** Questions not reached yet cannot be opened. */
   locked: (key: TRailKey) => boolean
}) => {
   const answered = ASKED_ROWS.filter((r) => done.includes(r.key)).length
   return (
      <aside
         className="rounded-[14px] bg-white/[0.04] p-2.5 flex flex-col lg:h-full gap-1"
         data-testid="cf-rail"
      >
         <div className="flex items-center justify-between px-3 pt-1.5 pb-1">
            <h4 className="text-[15px] font-semibold">Your campaign</h4>
            <span className="text-xs text-[#9ca3af]">{answered} of 3</span>
         </div>
         <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
            {ASKED_ROWS.map((row, i) => {
               const state =
                  row.key === current
                     ? 'cur'
                     : done.includes(row.key)
                       ? 'done'
                       : 'todo'
               return (
                  <RailRow
                     key={row.key}
                     row={row}
                     n={i + 1}
                     state={state}
                     value={state === 'todo' ? undefined : values[row.key]}
                     onClick={
                        locked(row.key) || row.key === current
                           ? undefined
                           : () => onGo(row.key)
                     }
                  />
               )
            })}
         </div>
         <div className="px-3 pt-4 pb-1 text-[11px] font-semibold tracking-[.08em] uppercase text-[#818181]">
            Ready for you
         </div>
         <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
            {READY_ROWS.map((row) => (
               <RailRow
                  key={row.key}
                  row={row}
                  state={row.key === current ? 'cur' : 'ready'}
                  value={values[row.key]}
                  onClick={
                     row.key === current ? undefined : () => onGo(row.key)
                  }
               />
            ))}
         </div>
         <div className="mt-auto hidden lg:block px-3 pb-1.5">
            <div className="h-1 rounded-full bg-white/[0.08] relative">
               <i
                  className="absolute left-0 top-0 h-1 rounded-full bg-[#9061DA] transition-all"
                  style={{ width: `${Math.round((answered / 3) * 100)}%` }}
               />
            </div>
         </div>
      </aside>
   )
}

/* ---------- Footer: Back left, hint centre, primary right. Never moves. ---------- */

export interface IFooter {
   backLabel?: string
   onBack?: () => void
   hint?: ReactNode
   primary: string
   onPrimary: () => void
   disabled?: boolean
   busy?: boolean
   variant?: 'primary' | 'launch'
   testId?: string
   /** Replaces the Back button (e.g. Discard changes). */
   left?: ReactNode
}

export const StepFooter = ({
   backLabel = 'Back',
   onBack,
   hint,
   primary,
   onPrimary,
   disabled,
   busy,
   variant = 'primary',
   testId = 'cf-primary',
   left,
}: IFooter) => (
   <div
      className="shrink-0 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 px-4 md:px-6 py-4 md:h-[92px] border-t border-white/[0.06]"
      data-testid="cf-footer"
   >
      {left ??
         (onBack ? (
            <button
               type="button"
               onClick={onBack}
               className="sm:w-[160px] h-12 rounded-full bg-white/[0.08] hover:bg-white/[0.12] flex items-center justify-center gap-2 text-[15px] font-semibold shrink-0"
               data-testid="cf-back"
            >
               <FlowIcon name="arrowLeft" size={16} /> {backLabel}
            </button>
         ) : (
            <div className="hidden sm:block w-[160px] shrink-0" />
         ))}
      <div className="flex-1 text-center text-[13px] leading-[18px] text-[#818181] px-2 hidden sm:block">
         {hint}
      </div>
      <button
         type="button"
         onClick={onPrimary}
         disabled={disabled || busy}
         data-testid={testId}
         className={cn(
            'sm:min-w-[260px] h-12 rounded-full flex items-center justify-center gap-2.5 text-[15px] font-semibold shrink-0 whitespace-nowrap px-6 transition-colors',
            disabled
               ? 'bg-white/[0.08] text-white/40 cursor-not-allowed'
               : variant === 'launch'
                 ? 'bg-gradient-to-r from-[#753A88] to-[#CC2B5E] text-white hover:brightness-110'
                 : 'bg-[#9061DA] hover:bg-[#7044B4] text-white'
         )}
      >
         {busy && (
            <span className="cf-spin inline-block w-4 h-4 rounded-full border-2 border-white/30 border-t-white" />
         )}
         {primary}{' '}
         {!busy && !disabled && (
            <FlowIcon
               name={variant === 'launch' ? 'rocket' : 'chevs'}
               size={18}
            />
         )}
      </button>
   </div>
)

/* ---------- Panel and step heading ---------- */

export const Panel = ({
   children,
   testId,
}: {
   children: ReactNode
   testId?: string
}) => (
   <section
      className="relative min-w-0 lg:h-full min-h-0 rounded-[14px] bg-white/[0.03] border border-white/[0.06] flex flex-col overflow-hidden"
      data-testid={testId}
   >
      {/* Steps render [content, StepFooter]; on desktop only the content scrolls (campaign-flow.css .cf-stepwrap). */}
      <div className="cf-stepwrap flex flex-col flex-1 min-h-0">{children}</div>
   </section>
)

/** Small disc, the question, one short line. */
export const StepHeading = ({
   icon,
   title,
   lede,
   align = 'center',
   right,
}: {
   icon?: TFlowIcon
   title: ReactNode
   lede?: ReactNode
   align?: 'center' | 'left'
   right?: ReactNode
}) => (
   <div
      className={cn(
         'flex gap-3',
         align === 'center'
            ? 'flex-col items-center text-center'
            : 'items-center'
      )}
   >
      {icon && (
         <span
            className={cn(
               'rounded-full bg-[#9061DA]/20 text-[#DAC2FF] hidden sm:flex items-center justify-center shrink-0',
               align === 'center' ? 'w-12 h-12' : 'w-10 h-10'
            )}
         >
            <FlowIcon name={icon} size={align === 'center' ? 20 : 17} />
         </span>
      )}
      <div className={cn('min-w-0', align === 'left' && 'flex-1')}>
         <h1 className="text-[22px] md:text-2xl leading-8 font-semibold tracking-[-.02em]">
            {title}
         </h1>
         {lede && (
            <p className="text-sm leading-5 text-[#9ca3af] mt-0.5">{lede}</p>
         )}
      </div>
      {right}
   </div>
)
